import React from 'react';
import type { FormRatingIcon } from '../../types/formFieldSettings';

interface FormRatingIconGlyphProps {
  icon: FormRatingIcon;
  filled?: boolean;
  className?: string;
}

export const FormRatingIconGlyph: React.FC<FormRatingIconGlyphProps> = ({
  icon,
  filled = false,
  className = 'h-7 w-7',
}) => {
  const stroke = filled ? 'currentColor' : 'currentColor';
  const fill = filled ? 'currentColor' : 'none';

  switch (icon)
  {
    case 'heart':
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          className={className}
          fill={fill}
          stroke={stroke}
          strokeWidth={1.8}
          aria-hidden
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
          />
        </svg>
      );
    case 'thumb':
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          className={className}
          fill={fill}
          stroke={stroke}
          strokeWidth={1.8}
          aria-hidden
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M14 9V5a3 3 0 00-3-3l-4 9v11h11.28a2 2 0 002-1.7l1.38-9a2 2 0 00-2-2.3H14zM7 22H4a2 2 0 01-2-2v-7a2 2 0 012-2h3"
          />
        </svg>
      );
    case 'smiley':
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          className={className}
          fill={fill}
          stroke={stroke}
          strokeWidth={1.8}
          aria-hidden
        >
          <circle cx="12" cy="12" r="9" />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M9 10h.01M15 10h.01M8.5 14.5s1.5 2 3.5 2 3.5-2 3.5-2"
            fill="none"
          />
        </svg>
      );
    case 'star':
    default:
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          className={className}
          fill={fill}
          stroke={stroke}
          strokeWidth={1.8}
          aria-hidden
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z"
          />
        </svg>
      );
  }
};

interface FormRatingScaleProps {
  scale: number;
  icon: FormRatingIcon;
  value?: number;
  disabled?: boolean;
  interactive?: boolean;
  onChange?: (value: number) => void;
}

export const FormRatingScale: React.FC<FormRatingScaleProps> = ({
  scale,
  icon,
  value = 0,
  disabled = false,
  interactive = false,
  onChange,
}) => {
  const levels = Array.from({ length: scale }, (_, index) => index + 1);
  const accentClass =
    icon === 'heart'
      ? 'text-rose-500'
      : icon === 'thumb'
        ? 'text-blue-600'
        : icon === 'smiley'
          ? 'text-amber-500'
          : 'text-amber-400';

  return (
    <div className="flex flex-wrap gap-3">
      {levels.map((level) => {
        const filled = value >= level;
        const content = (
          <>
            <span className="text-xs font-medium text-gray-500">{level}</span>
            <span className={filled ? accentClass : 'text-gray-300'}>
              <FormRatingIconGlyph icon={icon} filled={filled} />
            </span>
          </>
        );

        if (!interactive)
        {
          return (
            <div
              key={level}
              className="flex min-w-[2.25rem] flex-col items-center gap-1"
            >
              {content}
            </div>
          );
        }

        return (
          <button
            key={level}
            type="button"
            disabled={disabled}
            aria-label={`Classificação ${level} de ${scale}`}
            aria-pressed={value === level}
            onClick={() => onChange?.(level)}
            className="flex min-w-[2.25rem] flex-col items-center gap-1 rounded-md p-1 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {content}
          </button>
        );
      })}
    </div>
  );
};
